/**
 * DECKSTR.GG — Arena Parser: State Machine (Browser Edition)
 * ───────────────────────────────────────────────────────────
 * Processes GRE_TO_CLIENT messages to build a snapshot of the current
 * game state. Tracks zones, annotations (ObjectIdChanged, ZoneTransfer,
 * ResolutionStart/Complete, TappedUntapped), and playable overlays.
 *
 * Ported from bot/src/arena/parser.js — logic preserved,
 * converted to TypeScript ES module for browser use.
 */

import type { RouterEvent } from './router';

// ─── Types ────────────────────────────────────────

export interface CardRef {
    instanceId: number;
    grpId: number | null;
    isTapped?: boolean | null;
    ownerSeatId?: number | null;
    controllerSeatId?: number | null;
    cardTypes?: string[];
    subtypes?: string[];
}

export interface ZoneTransfer {
    instanceId: number;
    category: string | null;
    fromZoneId: number | null;
    toZoneId: number | null;
    srcZoneType: string | null;
    dstZoneType: string | null;
    affectorId: number | null;
}

export interface ObjectIdChange {
    oldId: number;
    newId: number;
}

export interface ResolutionEvent {
    annotationId: number | null;
    eventType: 'start' | 'complete';
    grpid: number | null;
    affectorId: number | null;
    affectorOwnerSeatId: number | null;
    affectorControllerSeatId: number | null;
    affectorGrpId: number | null;
}

export interface TurnInfo {
    turnNumber: number | null;
    activePlayer: number | null;
    phase: string | null;
    step: string | null;
    priorityPlayer: number | null;
    decisionPlayer: number | null;
}

export interface OppBoard {
    creatures: CardRef[];
    untappedCreatures: CardRef[];
    lands: CardRef[];
    nonlands: CardRef[];
    onStack: CardRef[];
    handSize: number;
    untappedLandCount: number;
    openMana: number;
    totalCreatureCount: number;
    blockerCount: number;
}

export interface ParserSnapshot {
    matchId: string | null;
    mySeatId: number | null;
    turnInfo: TurnInfo | null;
    opponentLifeTotal: number | null;
    myLifeTotal: number | null;
    myCardsInHand: CardRef[];
    myCardsOnStack: CardRef[];
    myCardsInPlay: CardRef[];
    myLandsInPlay: CardRef[];
    myCardsInGraveyard: CardRef[];
    myCardsInExile: { playable: CardRef[]; unplayable: CardRef[] };
    myHandSize: number;
    myManaPool: any;
    oppBoard: OppBoard;
    zoneTransfersThisDiff: ZoneTransfer[];
    objectIdChangesThisDiff: ObjectIdChange[];
    resolutionEventsThisDiff: ResolutionEvent[];
    playableInstanceIds: number[];
}

// ─── Internal Types ───────────────────────────────

interface ZoneMeta {
    type: string | null;
    ownerSeatId: number | null;
}

interface ObjectMeta {
    instanceId: number;
    grpId: number | null;
    ownerSeatId: number | null;
    controllerSeatId: number | null;
    zoneId: number | null;
    cardTypes: string[];
    subtypes: string[];
    superTypes: string[];
    isTapped: boolean | null;
    name: string | null;
}

// ─── ArenaParser Class ────────────────────────────

export class ArenaParser {
    private _mySeatId: number | null;
    private _zones: Map<number, Set<number>>;
    private _zoneMeta: Map<number, ZoneMeta>;
    private _objects: Map<number, ObjectMeta>;
    private _alias: Map<number, number>;
    private _aliasReverse: Map<number, Set<number>>;
    private _zoneTransfersThisDiff: ZoneTransfer[];
    private _objectIdChangesThisDiff: ObjectIdChange[];
    private _resolutionEventsThisDiff: ResolutionEvent[];
    private _tappedChangesThisDiff: Array<{ instanceId: number; tapped: boolean }>;
    private _turnInfo: TurnInfo | null;
    private _opponentLifeTotal: number | null;
    private _myLifeTotal: number | null;
    private _matchId: string | null;
    private _oppSeatId: number | null;
    private _myManaPool: any;
    private _oppManaPool: any;
    private _playableIds: Set<number>;

    constructor({ mySeatId = null }: { mySeatId?: number | null } = {}) {
        this._mySeatId = mySeatId;
        this._zones = new Map();
        this._zoneMeta = new Map();
        this._objects = new Map();
        this._alias = new Map();
        this._aliasReverse = new Map();
        this._zoneTransfersThisDiff = [];
        this._objectIdChangesThisDiff = [];
        this._resolutionEventsThisDiff = [];
        this._tappedChangesThisDiff = [];
        this._turnInfo = null;
        this._opponentLifeTotal = null;
        this._myLifeTotal = null;
        this._matchId = null;
        this._oppSeatId = null;
        this._myManaPool = null;
        this._oppManaPool = null;
        this._playableIds = new Set();
    }

    reset({ mySeatId = null }: { mySeatId?: number | null } = {}): void {
        this._mySeatId = mySeatId;
        this._zones.clear();
        this._zoneMeta.clear();
        this._objects.clear();
        this._alias.clear();
        this._aliasReverse.clear();
        this._zoneTransfersThisDiff = [];
        this._objectIdChangesThisDiff = [];
        this._resolutionEventsThisDiff = [];
        this._tappedChangesThisDiff = [];
        this._turnInfo = null;
        this._opponentLifeTotal = null;
        this._myLifeTotal = null;
        this._matchId = null;
        this._oppSeatId = null;
        this._myManaPool = null;
        this._oppManaPool = null;
        this._playableIds = new Set();
    }

    setMySeatId(seatId: number): void {
        this._mySeatId = seatId;
    }

    processEvent(event: RouterEvent): void {
        if (!event || event.kind !== 'GRE_TO_CLIENT') return;

        const messages = event.payload?.greToClientEvent?.greToClientMessages;
        if (!Array.isArray(messages)) return;

        this._zoneTransfersThisDiff = [];
        this._objectIdChangesThisDiff = [];
        this._resolutionEventsThisDiff = [];
        this._tappedChangesThisDiff = [];

        for (const msg of messages) {
            this._processMessage(msg);
        }
    }

    getSnapshot(): ParserSnapshot {
        const myZones = this._buildMyZones();
        const oppBoard = this._buildOppBoard();
        return {
            matchId: this._matchId,
            mySeatId: this._mySeatId,
            turnInfo: this._turnInfo ? { ...this._turnInfo } : null,
            opponentLifeTotal: this._opponentLifeTotal,
            myLifeTotal: this._myLifeTotal,
            myCardsInHand: myZones.hand,
            myCardsOnStack: myZones.stack,
            myCardsInPlay: myZones.battlefieldNonlands,
            myLandsInPlay: myZones.lands,
            myCardsInGraveyard: myZones.graveyard,
            myCardsInExile: {
                playable: myZones.exilePlayable,
                unplayable: myZones.exileGone,
            },
            myHandSize: myZones.hand.length,
            myManaPool: this._myManaPool,
            oppBoard,
            zoneTransfersThisDiff: [...this._zoneTransfersThisDiff],
            objectIdChangesThisDiff: [...this._objectIdChangesThisDiff],
            resolutionEventsThisDiff: [...this._resolutionEventsThisDiff],
            playableInstanceIds: [...this._playableIds],
        };
    }

    // ─── Internal Message Processing ──────────────

    private _processMessage(msg: any): void {
        if (!msg || typeof msg !== 'object') return;

        const type = msg.type;

        if (type === 'GREMessageType_GameStateMessage') {
            this._applyGameState(msg.gameStateMessage);
            return;
        }

        if (type === 'GREMessageType_QueuedGameStateMessage') {
            this._applyGameState(msg.gameStateMessage);
            return;
        }

        if (type === 'GREMessageType_ConnectResp') {
            if (msg.connectResp?.gameStateMessage) {
                this._applyGameState(msg.connectResp.gameStateMessage);
            }
            return;
        }
    }

    private _applyGameState(gsm: any): void {
        if (!gsm || typeof gsm !== 'object') return;

        // Turn info
        if (gsm.turnInfo) {
            this._turnInfo = {
                turnNumber: gsm.turnInfo.turnNumber ?? null,
                activePlayer: gsm.turnInfo.activePlayer ?? null,
                phase: gsm.turnInfo.phase ?? null,
                step: gsm.turnInfo.step ?? null,
                priorityPlayer: gsm.turnInfo.priorityPlayer ?? null,
                decisionPlayer: gsm.turnInfo.decisionPlayer ?? null,
            };
        }

        // Players — extract life totals and seat IDs
        if (Array.isArray(gsm.players)) {
            for (const player of gsm.players) {
                if (!player) continue;
                const seatId = player.systemSeatNumber ?? player.seatId;
                const life = player.lifeTotal ?? player.startingLifeTotal;

                if (this._mySeatId != null) {
                    if (seatId === this._mySeatId) {
                        if (typeof life === 'number') this._myLifeTotal = life;
                    } else {
                        this._oppSeatId = seatId;
                        if (typeof life === 'number') this._opponentLifeTotal = life;
                    }
                }

                if (seatId === this._mySeatId && Array.isArray(player.manaPool)) {
                    this._myManaPool = player.manaPool;
                }
                if (this._oppSeatId != null && seatId === this._oppSeatId && Array.isArray(player.manaPool)) {
                    this._oppManaPool = player.manaPool;
                }
            }
        }

        // Zones
        if (Array.isArray(gsm.zones)) {
            for (const zone of gsm.zones) {
                if (!zone) continue;
                const zoneId = zone.zoneId;
                if (zoneId == null) continue;

                this._zoneMeta.set(zoneId, {
                    type: zone.type ?? null,
                    ownerSeatId: zone.ownerSeatId ?? null,
                });

                if (Array.isArray(zone.objectInstanceIds)) {
                    this._zones.set(zoneId, new Set(zone.objectInstanceIds));
                }
            }
        }

        // Game objects
        if (Array.isArray(gsm.gameObjects)) {
            for (const obj of gsm.gameObjects) {
                if (!obj) continue;
                const iid = obj.instanceId;
                if (iid == null) continue;

                this._objects.set(iid, {
                    instanceId: iid,
                    grpId: obj.grpId ?? null,
                    ownerSeatId: obj.ownerSeatId ?? null,
                    controllerSeatId: obj.controllerSeatId ?? null,
                    zoneId: obj.zoneId ?? null,
                    cardTypes: obj.cardTypes ?? [],
                    subtypes: obj.subtypes ?? [],
                    superTypes: obj.superTypes ?? [],
                    isTapped: obj.isTapped ?? null,
                    name: obj.name ?? null,
                });
            }
        }

        // Diff deletions
        if (gsm.diffDeletedInstanceIds) {
            for (const iid of gsm.diffDeletedInstanceIds) {
                this._objects.delete(iid);
            }
        }

        // Annotations
        if (Array.isArray(gsm.annotations)) {
            this._applyAnnotations(gsm.annotations);
        }

        // Playable overlay
        if (Array.isArray(gsm.actions)) {
            this._playableIds.clear();
            for (const action of gsm.actions) {
                if (action?.instanceId != null) {
                    this._playableIds.add(action.instanceId);
                }
                if (action?.grpId != null) {
                    for (const [iid, obj] of this._objects) {
                        if (obj.grpId === action.grpId) {
                            this._playableIds.add(iid);
                        }
                    }
                }
            }
        }
    }

    private _applyAnnotations(annotations: any[]): void {
        for (const a of annotations) {
            if (!a || !Array.isArray(a.type)) continue;

            // ObjectIdChanged
            if (a.type.includes('AnnotationType_ObjectIdChanged')) {
                const orig = this._readDetailInt(a, 'orig_id');
                const next = this._readDetailInt(a, 'new_id');
                if (typeof orig === 'number' && typeof next === 'number') {
                    this._alias.set(orig, next);
                    this._addAliasReverse(next, orig);
                    this._objectIdChangesThisDiff.push({ oldId: orig, newId: next });
                    this._migrateMetadataOnly(orig, next);
                }
                continue;
            }

            // ResolutionStart / ResolutionComplete
            if (
                a.type.includes('AnnotationType_ResolutionStart') ||
                a.type.includes('AnnotationType_ResolutionComplete')
            ) {
                const eventType = a.type.includes('AnnotationType_ResolutionComplete')
                    ? 'complete' as const
                    : 'start' as const;
                const grpid = this._readDetailInt(a, 'grpid');
                const affectorId = Number.isInteger(a?.affectorId) ? a.affectorId : null;
                const affectorMeta = Number.isInteger(affectorId)
                    ? this._getBestObjectMeta(affectorId!)
                    : null;

                this._resolutionEventsThisDiff.push({
                    annotationId: Number.isInteger(a?.id) ? a.id : null,
                    eventType,
                    grpid: Number.isInteger(grpid) ? grpid : null,
                    affectorId,
                    affectorOwnerSeatId: affectorMeta?.ownerSeatId ?? null,
                    affectorControllerSeatId: affectorMeta?.controllerSeatId ?? null,
                    affectorGrpId: affectorMeta?.grpId ?? null,
                });
                continue;
            }

            // TappedUntapped
            if (a.type.includes('AnnotationType_TappedUntappedPermanent')) {
                const tappedInt = this._readDetailInt(a, 'tapped');
                const tapped = tappedInt === 1 ? true : tappedInt === 0 ? false : null;

                if (tapped != null) {
                    const affected = Array.isArray(a?.affectedIds) ? a.affectedIds : [];
                    for (const id of affected) {
                        if (!Number.isInteger(id)) continue;
                        const obj = this._objects.get(id);
                        if (obj) obj.isTapped = tapped;
                        this._tappedChangesThisDiff.push({ instanceId: id, tapped });
                    }
                }
                continue;
            }

            // ZoneTransfer
            if (a.type.includes('AnnotationType_ZoneTransfer')) {
                const category = this._readDetailString(a, 'category') ?? null;
                const affected = Array.isArray(a?.affectedIds)
                    ? a.affectedIds.filter((id: any) => typeof id === 'number')
                    : [];
                const fromZoneId = this._readDetailInt(a, 'zone_src')
                    ?? this._readDetailInt(a, 'from_zone_id') ?? null;
                const toZoneId = this._readDetailInt(a, 'zone_dest')
                    ?? this._readDetailInt(a, 'to_zone_id') ?? null;
                const affectorId = Number.isInteger(a?.affectorId) ? a.affectorId : null;

                const srcMeta = fromZoneId != null ? this._zoneMeta.get(fromZoneId) : null;
                const dstMeta = toZoneId != null ? this._zoneMeta.get(toZoneId) : null;

                for (const iid of affected) {
                    this._zoneTransfersThisDiff.push({
                        instanceId: iid,
                        category,
                        fromZoneId,
                        toZoneId,
                        srcZoneType: srcMeta?.type ?? null,
                        dstZoneType: dstMeta?.type ?? null,
                        affectorId,
                    });
                }
                continue;
            }
        }
    }

    // ─── Zone Building ────────────────────────────

    private _buildMyZones() {
        const result: {
            hand: CardRef[];
            stack: CardRef[];
            battlefieldNonlands: CardRef[];
            lands: CardRef[];
            graveyard: CardRef[];
            exilePlayable: CardRef[];
            exileGone: CardRef[];
        } = {
            hand: [],
            stack: [],
            battlefieldNonlands: [],
            lands: [],
            graveyard: [],
            exilePlayable: [],
            exileGone: [],
        };

        for (const [zoneId, instanceIds] of this._zones) {
            const meta = this._zoneMeta.get(zoneId);
            if (!meta) continue;

            const ownedByMe = this._mySeatId == null || meta.ownerSeatId === this._mySeatId;
            const zoneType = (meta.type || '').toLowerCase();

            for (const iid of instanceIds) {
                const obj = this._objects.get(iid);
                if (!obj) continue;

                const controlledByMe = this._mySeatId == null
                    || obj.controllerSeatId === this._mySeatId
                    || obj.ownerSeatId === this._mySeatId;

                const ref: CardRef = {
                    instanceId: iid,
                    grpId: obj.grpId,
                    isTapped: obj.isTapped,
                    ownerSeatId: obj.ownerSeatId,
                    controllerSeatId: obj.controllerSeatId,
                };

                if (zoneType.includes('hand') && ownedByMe) {
                    result.hand.push(ref);
                } else if (zoneType.includes('stack')) {
                    if (controlledByMe) result.stack.push(ref);
                } else if (zoneType.includes('battlefield')) {
                    if (controlledByMe) {
                        if (this._isLandType(obj)) result.lands.push(ref);
                        else result.battlefieldNonlands.push(ref);
                    }
                } else if (zoneType.includes('graveyard') && ownedByMe) {
                    result.graveyard.push(ref);
                } else if (zoneType.includes('exile')) {
                    if (ownedByMe || controlledByMe) {
                        if (this._playableIds.has(iid)) {
                            result.exilePlayable.push(ref);
                        } else {
                            result.exileGone.push(ref);
                        }
                    }
                }
            }
        }

        return result;
    }

    private _buildOppBoard(): OppBoard {
        const creatures: CardRef[] = [];
        const lands: CardRef[] = [];
        const nonlands: CardRef[] = [];
        const onStack: CardRef[] = [];
        let handSize = 0;

        for (const [zoneId, instanceIds] of this._zones) {
            const meta = this._zoneMeta.get(zoneId);
            if (!meta) continue;

            const zoneType = (meta.type || '').toLowerCase();

            for (const iid of instanceIds) {
                const obj = this._objects.get(iid);
                if (!obj) continue;

                const isOpp = (this._mySeatId != null) && (
                    obj.controllerSeatId !== this._mySeatId &&
                    obj.ownerSeatId !== this._mySeatId
                );
                const isOppZone = (this._mySeatId != null) &&
                    (meta.ownerSeatId !== this._mySeatId);

                if (!isOpp && !isOppZone) continue;

                const ref: CardRef = {
                    instanceId: iid,
                    grpId: obj.grpId,
                    isTapped: obj.isTapped,
                    cardTypes: obj.cardTypes || [],
                    subtypes: obj.subtypes || [],
                };

                if (zoneType.includes('battlefield')) {
                    if (this._isLandType(obj)) lands.push(ref);
                    else if (this._isCreatureType(obj)) creatures.push(ref);
                    else nonlands.push(ref);
                } else if (zoneType.includes('hand') && isOppZone) {
                    handSize++;
                } else if (zoneType.includes('stack') && isOpp) {
                    onStack.push(ref);
                }
            }
        }

        const untappedCreatures = creatures.filter(c => !c.isTapped);
        const untappedLands = lands.filter(l => !l.isTapped).length;

        return {
            creatures,
            untappedCreatures,
            lands,
            nonlands,
            onStack,
            handSize,
            untappedLandCount: untappedLands,
            openMana: untappedLands,
            totalCreatureCount: creatures.length,
            blockerCount: untappedCreatures.length,
        };
    }

    private _isLandType(obj: ObjectMeta): boolean {
        return Array.isArray(obj.cardTypes) && obj.cardTypes.includes('CardType_Land');
    }

    private _isCreatureType(obj: ObjectMeta): boolean {
        return Array.isArray(obj.cardTypes) && obj.cardTypes.includes('CardType_Creature');
    }

    // ─── Annotation Detail Helpers ────────────────

    private _readDetailInt(annotation: any, key: string): number | null {
        if (!Array.isArray(annotation?.details)) return null;
        for (const d of annotation.details) {
            if (d?.key === key) {
                if (Array.isArray(d?.valueInt32) && d.valueInt32.length > 0) {
                    return d.valueInt32[0];
                }
                if (typeof d?.valueInt32 === 'number') return d.valueInt32;
            }
        }
        return null;
    }

    private _readDetailString(annotation: any, key: string): string | null {
        if (!Array.isArray(annotation?.details)) return null;
        for (const d of annotation.details) {
            if (d?.key === key) {
                if (Array.isArray(d?.valueString) && d.valueString.length > 0) {
                    return d.valueString[0];
                }
                if (typeof d?.valueString === 'string') return d.valueString;
            }
        }
        return null;
    }

    private _getBestObjectMeta(instanceId: number) {
        const obj = this._objects.get(instanceId);
        if (!obj) return null;
        return {
            grpId: obj.grpId ?? null,
            ownerSeatId: obj.ownerSeatId ?? null,
            controllerSeatId: obj.controllerSeatId ?? null,
        };
    }

    private _addAliasReverse(newId: number, origId: number): void {
        if (!this._aliasReverse.has(origId)) {
            this._aliasReverse.set(origId, new Set());
        }
        this._aliasReverse.get(origId)!.add(newId);
    }

    private _migrateMetadataOnly(origId: number, newId: number): void {
        const origObj = this._objects.get(origId);
        const newObj = this._objects.get(newId);
        if (origObj && !newObj) {
            this._objects.set(newId, { ...origObj, instanceId: newId });
        }
    }
}
